import { useMemo } from 'react';
import { motion } from 'framer-motion';

// Floating Orb Component
const FloatingOrb = ({ delay = 0, duration = 20, size = 400, color = 'orange', top, left, right, bottom }) => {
  const colors = {
    orange: 'rgba(249, 115, 22, 0.15)',
    amber: 'rgba(251, 191, 36, 0.12)',
    red: 'rgba(239, 68, 68, 0.1)',
    purple: 'rgba(168, 85, 247, 0.08)',
    blue: 'rgba(59, 130, 246, 0.08)',
    green: 'rgba(34, 197, 94, 0.08)',
  };

  return (
    <motion.div
      className="absolute rounded-full pointer-events-none"
      style={{
        width: size,
        height: size,
        background: `radial-gradient(circle, ${colors[color]} 0%, transparent 70%)`,
        filter: 'blur(40px)',
        top,
        left,
        right,
        bottom,
      }}
      animate={{
        x: [0, 30, -20, 40, 0],
        y: [0, -40, 20, -30, 0],
        scale: [1, 1.1, 0.95, 1.05, 1],
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />
  );
};

// Grid Pattern Component
const GridPattern = () => (
  <div 
    className="absolute inset-0 pointer-events-none opacity-[0.03]"
    style={{
      backgroundImage: `
        linear-gradient(rgba(249, 115, 22, 0.5) 1px, transparent 1px),
        linear-gradient(90deg, rgba(249, 115, 22, 0.5) 1px, transparent 1px)
      `,
      backgroundSize: '60px 60px',
    }}
  />
);

// Noise Texture Overlay
const NoiseOverlay = () => (
  <div 
    className="absolute inset-0 pointer-events-none opacity-[0.02]"
    style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
    }}
  />
);

// Animated Gradient Mesh
const GradientMesh = () => (
  <motion.div
    className="absolute inset-0 pointer-events-none"
    style={{
      background: `
        radial-gradient(ellipse 80% 50% at 20% 40%, rgba(249, 115, 22, 0.12) 0%, transparent 50%),
        radial-gradient(ellipse 60% 40% at 80% 20%, rgba(251, 191, 36, 0.1) 0%, transparent 50%),
        radial-gradient(ellipse 50% 50% at 50% 80%, rgba(239, 68, 68, 0.08) 0%, transparent 50%),
        radial-gradient(ellipse 40% 30% at 10% 90%, rgba(168, 85, 247, 0.06) 0%, transparent 50%)
      `,
    }}
    animate={{
      opacity: [0.8, 1, 0.9, 1, 0.8],
    }}
    transition={{
      duration: 8,
      repeat: Infinity,
      ease: 'easeInOut',
    }}
  />
);

// Floating Particles
const Particle = ({ delay, x, y }) => (
  <motion.div
    className="absolute w-1 h-1 rounded-full bg-orange-400/30"
    style={{ left: `${x}%`, top: `${y}%` }}
    animate={{
      y: [0, -100, -200],
      opacity: [0, 1, 0],
      scale: [0, 1, 0.5],
    }}
    transition={{
      duration: 8,
      delay,
      repeat: Infinity,
      ease: 'easeOut',
    }}
  />
);

// Glow Line Animation
const GlowLine = ({ direction = 'horizontal', delay = 0 }) => {
  const isHorizontal = direction === 'horizontal';
  
  return (
    <motion.div
      className={`absolute ${isHorizontal ? 'h-px w-full' : 'w-px h-full'} pointer-events-none`}
      style={{
        background: isHorizontal 
          ? 'linear-gradient(90deg, transparent, rgba(249, 115, 22, 0.3), transparent)'
          : 'linear-gradient(180deg, transparent, rgba(249, 115, 22, 0.3), transparent)',
        top: isHorizontal ? '30%' : 0,
        left: isHorizontal ? 0 : '70%',
      }}
      animate={{
        opacity: [0, 0.5, 0],
        [isHorizontal ? 'x' : 'y']: ['-100%', '100%'],
      }}
      transition={{
        duration: 4,
        delay,
        repeat: Infinity,
        ease: 'linear',
      }}
    />
  );
};

// Aurora Effect
const Aurora = () => (
  <motion.div
    className="absolute inset-0 pointer-events-none overflow-hidden"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 2 }}
  >
    <motion.div
      className="absolute -top-1/2 -left-1/2 w-full h-full"
      style={{
        background: 'conic-gradient(from 0deg at 50% 50%, rgba(249, 115, 22, 0.1) 0deg, rgba(251, 191, 36, 0.05) 60deg, transparent 120deg, rgba(239, 68, 68, 0.08) 180deg, rgba(168, 85, 247, 0.05) 240deg, transparent 300deg, rgba(249, 115, 22, 0.1) 360deg)',
        filter: 'blur(80px)',
      }}
      animate={{
        rotate: [0, 360],
      }}
      transition={{
        duration: 30,
        repeat: Infinity,
        ease: 'linear',
      }}
    />
  </motion.div>
);

// Pre-generate particle positions to avoid Math.random during render
const PARTICLE_DATA = [
  { id: 0, x: 15, y: 105, delay: 0.5 },
  { id: 1, x: 25, y: 110, delay: 1.2 },
  { id: 2, x: 35, y: 108, delay: 2.1 },
  { id: 3, x: 45, y: 115, delay: 0.8 },
  { id: 4, x: 55, y: 103, delay: 3.5 },
  { id: 5, x: 65, y: 112, delay: 1.8 },
  { id: 6, x: 75, y: 107, delay: 4.2 },
  { id: 7, x: 85, y: 118, delay: 2.5 },
  { id: 8, x: 20, y: 109, delay: 0.3 },
  { id: 9, x: 40, y: 114, delay: 3.8 },
  { id: 10, x: 60, y: 106, delay: 1.5 },
  { id: 11, x: 80, y: 111, delay: 4.8 },
  { id: 12, x: 30, y: 116, delay: 2.8 },
  { id: 13, x: 50, y: 104, delay: 0.9 },
  { id: 14, x: 70, y: 119, delay: 3.2 },
];

// Main Animated Background Component
const AnimatedBackground = ({ children }) => {
  const particles = useMemo(() => PARTICLE_DATA, []);

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Base Gradient */}
      <div 
        className="fixed inset-0"
        style={{
          background: 'linear-gradient(135deg, #fffbf5 0%, #fff7ed 25%, #fef3e2 50%, #fff9f0 75%, #fffbf5 100%)',
        }}
      />

      {/* Aurora Effect */}
      <Aurora />

      {/* Gradient Mesh */}
      <GradientMesh />

      {/* Floating Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <FloatingOrb delay={0} duration={25} size={600} color="orange" top="-10%" left="-5%" />
        <FloatingOrb delay={2} duration={20} size={500} color="amber" top="20%" right="-10%" />
        <FloatingOrb delay={4} duration={22} size={400} color="red" bottom="10%" left="20%" />
        <FloatingOrb delay={6} duration={28} size={450} color="purple" bottom="-5%" right="10%" />
        <FloatingOrb delay={3} duration={24} size={350} color="blue" top="50%" left="50%" />
      </div>

      {/* Grid Pattern */}
      <GridPattern />

      {/* Floating Particles */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {particles.map((particle) => (
          <Particle key={particle.id} {...particle} />
        ))}
      </div>

      {/* Glow Lines */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <GlowLine direction="horizontal" delay={0} />
        <GlowLine direction="vertical" delay={2} />
      </div>

      {/* Noise Overlay */}
      <NoiseOverlay />

      {/* Vignette Effect */}
      <div 
        className="fixed inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0, 0, 0, 0.02) 100%)',
        }}
      />

      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

export default AnimatedBackground;
